import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shows the daily-rotating scoring PIN to the tournament creator. The PIN is
 * hidden behind dots by default. Clicking the dots reveals it; clicking again
 * copies to clipboard. Rotates daily at midnight in the tournament's timezone.
 *
 * Props:
 *   - scoringPin: { pin, rotates_at, timezone }
 *   - showPin:    boolean
 *   - setShowPin: (boolean) => void
 */
export default function ScoringAccessCard({ scoringPin, showPin, setShowPin }) {
  const handleClick = () => {
    if (showPin && navigator.clipboard) {
      navigator.clipboard.writeText(scoringPin.pin).then(() => toast.success('PIN copied'));
    } else {
      setShowPin(true);
    }
  };

  return (
    <Card
      className="border-accent/40 bg-gradient-to-br from-accent/5 to-transparent"
      data-testid="scoring-access-card"
    >
      <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="h-12 w-12 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
            <Lock className="h-6 w-6 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base font-bold uppercase tracking-wide">Scoring Access PIN</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Share this PIN with umpires only. <span className="text-destructive font-semibold">Do not post it publicly.</span> This PIN stays the same for the entire tournament.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="font-mono font-bold text-2xl tracking-[0.3em] text-accent bg-background border-2 border-accent/30 rounded-md px-4 py-2 select-all cursor-pointer"
            onClick={handleClick}
            data-testid="scoring-access-card-pin"
            title={showPin ? 'Click to copy' : 'Click to reveal'}
          >
            {showPin ? scoringPin.pin : '••••'}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowPin(s => !s)}
            data-testid="toggle-pin-visibility"
            aria-label={showPin ? 'Hide PIN' : 'Reveal PIN'}
          >
            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
